import React, { useState } from "react";
import { Link } from "react-router-dom";

/**
 * The newest `TAIL_ALWAYS_SHOWN` always, up to `TAIL_MAX` behind a disclosure, and a link
 * to the rest.
 *
 * One component for every list on the operations page (finished jobs, notifications,
 * warnings), because they answer the same question in the same shape. A panel rendering
 * everything its response carried would be a list rewriting itself on every poll, with no
 * row readable.
 *
 * Past the ceiling a tail becomes a page that cannot be filtered or navigated, which is
 * what the link is for. None of these tables is pruned, so a panel that simply stopped
 * would read as though nothing came before.
 *
 * The cap is applied here rather than by the server, so it stays a rendering decision.
 */
export const TAIL_ALWAYS_SHOWN = 3;
/** The most rows a panel tail shows, with the disclosure open. */
export const TAIL_MAX = 10;

/**
 * A list panel's newest rows with a disclosure for more and a link to the full list.
 *
 * @param items The rows, newest first.
 * @param idOf A stable key for a row; at most one row is open at a time.
 * @param renderRow Renders a row, told whether it is open and how to toggle it.
 * @param href Where the full list lives.
 * @param linkLabel The text of the link to the full list.
 */
export function PanelTail<T>({
  items,
  idOf,
  renderRow,
  href,
  linkLabel,
}: {
  items: readonly T[];
  idOf: (item: T) => string;
  renderRow: (item: T, open: boolean, toggle: () => void) => React.ReactNode;
  href: string;
  linkLabel: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const capped = items.slice(0, TAIL_MAX);
  const shown = expanded ? capped : capped.slice(0, TAIL_ALWAYS_SHOWN);
  const hidden = capped.length - shown.length;

  return (
    <>
      <div className="filings live">
        {shown.map((item) => {
          const id = idOf(item);
          return (
            <React.Fragment key={id}>
              {renderRow(item, openId === id, () => setOpenId(openId === id ? null : id))}
            </React.Fragment>
          );
        })}
      </div>
      <div className="tailmore">
        {hidden > 0 && (
          <button
            type="button"
            className="iconbtn"
            aria-expanded={expanded}
            onClick={() => setExpanded(true)}
          >
            Show more
          </button>
        )}
        {expanded && capped.length > TAIL_ALWAYS_SHOWN && (
          <button type="button" className="iconbtn" onClick={() => setExpanded(false)}>
            Show fewer
          </button>
        )}
        <Link className="tailall" to={href}>
          {linkLabel} →
        </Link>
      </div>
    </>
  );
}

/** One labelled line inside an opened row. Absent values are said, never left blank. */
export function DetailLine({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="dl">
      <span className="dlk">{k}</span>
      <span className="dlv">{v}</span>
    </div>
  );
}

/**
 * A row that opens to show what the list had to cut: a truncated message, or an error
 * that would otherwise live only in a `title` attribute, invisible on a touch screen.
 */
export function ExpandableRow({
  open,
  onToggle,
  summary,
  detail,
}: {
  open: boolean;
  onToggle: () => void;
  summary: React.ReactNode;
  detail: React.ReactNode;
}) {
  return (
    <>
      <button type="button" className="filing rowbtn" aria-expanded={open} onClick={onToggle}>
        {summary}
      </button>
      {open && <div className="rowdetail">{detail}</div>}
    </>
  );
}
